/**
 * HeroSection Component
 * Redesigned hero with two-column layout:
 *  - Left: Riticket branding, tagline, subtitle, CTA
 *  - Right: FeatureCarousel
 * Navigation bar at top with home icon (left) and auth buttons (right)
 */

import React from 'react';
import { func } from 'prop-types';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import logo from '../../../../../assets/white-transparent-cropped.svg?url';
import FeatureCarousel from '../FeatureCarousel';
import classes from './HeroSection.module.css';

const HeroSection = ({ onLoginClick, onExploreClick }) => {
  const history = useHistory();

  const handleHomeClick = () => {
    history.push('/?from=home');
  };

  return (
    <section className={classes.hero}>
      {/* Background gradient */}
      <div className={classes.bgGradient} aria-hidden="true" />

      {/* Navigation bar */}
      <nav className={classes.navbar}>
        <button
          type="button"
          onClick={handleHomeClick}
          className={classes.homeButton}
          aria-label="Go to home page"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M3 10.75 12 3l9 7.75" />
            <path d="M5.5 9.75V21h4.75v-6h3.5v6h4.75V9.75" />
          </svg>
        </button>

        <div className={classes.authButtons}>
          <button
            type="button"
            className={classes.btnLogin}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onLoginClick(e);
            }}
          >
            Log in
          </button>
          <button
            type="button"
            className={classes.btnSignup}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onExploreClick(e);
            }}
          >
            Sign up
          </button>
        </div>
      </nav>

      {/* Main content area */}
      <div className={classes.content}>
        {/* Left column – branding & CTA */}
        <motion.div
          className={classes.leftColumn}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <img src={logo} alt="Riticket" className={classes.logo} />

          <h1 className={classes.heading}>
            Putting <span className={classes.headingHighlight}>real fans</span> back in control.
          </h1>

          <p className={classes.subtitle}>
            Find tickets, get price insights and connect with fans like you.
          </p>

          <button
            type="button"
            className={classes.ctaButton}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onExploreClick(e);
            }}
          >
            Join the crowd
          </button>
        </motion.div>

        {/* Right column – feature carousel */}
        <motion.div
          className={classes.rightColumn}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
        >
          <FeatureCarousel />
        </motion.div>
      </div>
    </section>
  );
};

HeroSection.propTypes = {
  onLoginClick: func.isRequired,
  onExploreClick: func.isRequired,
};

export default HeroSection;
